/** @import {JWTDecodeParams, JWTEncodeParams} from 'next-auth/jwt' */
/** @import {GetServerSidePropsContext} from 'next' */

import Cookies from 'cookies'
import { decode as decodeJWT, encode as encodeJWT } from 'next-auth/jwt'

export { withSecret }

/**
 * Server side function to load secret state from a signed and encrypted cookie.
 *
 * If the secret doesn't exist, has been tampered with or can't be decrypted, a
 * fallback function can be defined to provide a default value. If no such
 * fallback is defined, the default will be `undefined`.
 *
 * A validate function can be specified to validate the stored or fallback
 * value. Whatever the validation function returns will be written back to the
 * cookie, unless it is `undefined` in which case the cookie will be cleared.
 *
 * The secret is stored as an encrypted JWT, using a combination of the secret
 * and a salt based on the name of the cookie.
 *
 * @param {GetServerSidePropsContext} context
 * @param {string} name
 * @param {{
 *   secret?: string
 *   maxAge?: number
 *   fallback?: () => Promise<any>
 *   validate?: (value: any) => Promise<any>
 *   sameSite?: 'strict' | 'lax'
 * }} [options]
 */
async function withSecret(context, name, options = {}) {
  // Pluck these dynamically to avoid NextJS compiling these into the build
  const { NEXTAUTH_SECRET } = process.env

  const {
    secret = NEXTAUTH_SECRET,
    maxAge,
    fallback,
    validate,
    sameSite = 'strict',
  } = options

  if (!secret) {
    throw new Error('secret must be defined')
  }

  const salt = `${secret}::${name}`

  const cookies = new Cookies(context.req, context.res, {
    keys: [secret],
  })

  /** @type {Cookies.SetOption} */
  const optionsSet = {
    httpOnly: true,
    signed: true,
    maxAge,
    sameSite,
    // TODO: Replae with `NEXT_PUBLIC_APP_URL.startsWith('https:')` once we
    // fix APP_URL so they don't have to be the same
    secure: false,
  }

  /** @type {Cookies.GetOption} */
  const optionsGet = {
    signed: optionsSet.signed,
  }

  const token = await decode({
    token: cookies.get(name, optionsGet),
    secret,
    salt,
  })

  try {
    const data = token === undefined ? await fallback?.() : token.data
    const validatedData = await validate?.(data)

    if (validatedData === undefined) {
      cookies.set(name, undefined, optionsSet) // Clear cookie
    } else {
      cookies.set(
        name,
        await encode({ token: { data: validatedData }, secret, salt }),
        optionsSet,
      )
    }

    return validatedData
  } catch (error) {
    // Debug logging cookie errors, this is stripped out in production builds
    console.debug('Unable to read cookie.', error)
    cookies.set(name) // Clear cookie on errors
  }
}

/**
 * Safe version of next-auth/jwt.decode that never throws.
 *
 * @param {JWTDecodeParams} params
 */
async function decode({ token, ...params }) {
  if (token === undefined) {
    return
  }

  try {
    return decodeJWT({ token, ...params })
  } catch {
    /* empty */
  }
}

/**
 * Safe version of next-auth/jwt.encode that never throws.
 *
 * @param {JWTEncodeParams} params
 */
async function encode({ token, ...params }) {
  if (token === undefined) {
    return
  }

  try {
    return encodeJWT({ token, ...params })
  } catch {
    /* empty */
  }
}
